import type { TasklistPageBlock, TasklistPageSection } from '@/types/tasklistPageJson';

export const TASKLIST_QUICK_LINKS_SECTION_TITLE = 'Quick Links';

type ButtonBlock = Extract<TasklistPageBlock, { type: 'button' }>;
type QuickLinkBlock = Extract<TasklistPageBlock, { type: 'quickLink' }>;
type TextBlock = Extract<TasklistPageBlock, { type: 'text' }>;

export type TasklistSectionRenderUnit =
  | { kind: 'block'; block: TasklistPageBlock; blockIndex: number }
  | { kind: 'quickLink'; quickLink: QuickLinkBlock; blockIndex: number };

/** Parses `#### Title` with optional body from markdown (school overview Quick Links). */
export function parseQuickLinkHeadingMarkdown(markdown: string): { title: string; body: string } | null {
  const trimmed = markdown.trim();
  const match = trimmed.match(/^####\s+(.+?)(?:\n+([\s\S]*))?$/);
  if (!match) return null;
  return { title: match[1].trim(), body: (match[2] ?? '').trim() };
}

/** Short intro + CTA pairs (e.g. daily-routines OLS sections) — not long multi-part guides. */
export function isCompactQuickLinkIntro(markdown: string): boolean {
  const text = markdown.trim();
  if (!text) return false;
  if (/^\*\*.+\*\*/.test(text) && text.includes('\n\n**')) return false;
  if (text.split('\n\n').length > 2) return false;
  return text.length <= 500;
}

function isTextBlock(block: TasklistPageBlock): block is TextBlock {
  return block.type === 'text';
}

function isButtonBlock(block: TasklistPageBlock | undefined): block is ButtonBlock {
  return block != null && block.type === 'button';
}

function textButtonToQuickLink(text: TextBlock, button: ButtonBlock): QuickLinkBlock {
  const heading = parseQuickLinkHeadingMarkdown(text.markdown);
  if (heading) {
    return {
      type: 'quickLink',
      heading: heading.title,
      ...(heading.body ? { description: heading.body } : {}),
      buttonUrl: button.buttonUrl,
      buttonLabel: button.buttonLabel,
      ...(button.linkMeta ? { linkMeta: button.linkMeta } : {}),
    };
  }
  return {
    type: 'quickLink',
    description: text.markdown,
    buttonUrl: button.buttonUrl,
    buttonLabel: button.buttonLabel,
    ...(button.linkMeta ? { linkMeta: button.linkMeta } : {}),
  };
}

/**
 * Groups legacy `text` + `button` pairs into quick-link cards for render.
 * Explicit `quickLink` blocks pass through unchanged.
 */
export function planTasklistSectionRenderUnits(section: TasklistPageSection): TasklistSectionRenderUnit[] {
  const blocks = section.blocks ?? [];
  const isQuickLinksSection = section.title.trim() === TASKLIST_QUICK_LINKS_SECTION_TITLE;
  const units: TasklistSectionRenderUnit[] = [];
  let i = 0;

  while (i < blocks.length) {
    const block = blocks[i];
    if (block == null) {
      i += 1;
      continue;
    }
    const next = blocks[i + 1];

    if (block.type === 'quickLink') {
      units.push({ kind: 'quickLink', quickLink: block, blockIndex: i });
      i += 1;
      continue;
    }

    if (isTextBlock(block) && isButtonBlock(next)) {
      const heading = parseQuickLinkHeadingMarkdown(block.markdown);
      const useQuickLink = isQuickLinksSection
        ? heading !== null
        : isCompactQuickLinkIntro(block.markdown) && heading === null;
      if (useQuickLink) {
        units.push({
          kind: 'quickLink',
          quickLink: textButtonToQuickLink(block, next),
          blockIndex: i,
        });
        i += 2;
        continue;
      }
    }

    units.push({ kind: 'block', block, blockIndex: i });
    i += 1;
  }

  return units;
}

export function sectionUsesQuickLinkStack(units: TasklistSectionRenderUnit[]): boolean {
  return units.length > 1 && units.every((u) => u.kind === 'quickLink');
}
